import { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";

//Components
import { Grid, Typography, Container, Paper, Divider } from "@mui/material";
import DataView from 'Components/DataView';
import { Button } from "@gpstrackit/react-controls";
import { NavLink } from "react-router-dom";
import NewMigration from "Modules/importP4/views/main/.components/newMigration";
import ExceptionDetails from "Modules/importP4/views/main/.components/exceptionDetails";
import { Loading } from "@gpstrackit/react-controls";
//Helpers
import { getDateFormatted, idsValidation } from 'Core/data/Helpers';
import { HasPermission } from "Core/security";
import { endpoints } from 'Core/defaultValues';
//Actions
import {
    accountsImportOpenModal,
    exceptionDetailsOpenModal,
    searchEntityRequest
} from 'Redux/actions';

import { useTokenOneTimeHandler } from 'Core/hooks';

const ImportP4Main = (props) => {
    const { messages } = useIntl();
    const tableRef = useRef(null);
    const dispatch = useDispatch();

    const user = useSelector(state => state.securityRedux.user);
    const dealers = useSelector(state => state.generalRedux.dealers);
    const tokenOneTime = useSelector(state => state.accountOverviewRedux.tokenOneTime);
    const loadingToken = useSelector(state => state.accountOverviewRedux.loadingToken);
    const { handleAuthenticate } =  useTokenOneTimeHandler(tokenOneTime, dealers);
    //local states
    const [total, setTotal] = useState(0);
    const [columns, setColumns] = useState([
        {
            key: "id",
            title: "Id",
            width: 50,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row?.company?.id',
            render: (item) => {
                return (
                    <span>{item.company?.id}</span>
                )
            }
        },
        {
            key: "sourceEntityId",
            title: messages['import_account_external_id'],
            width: 150,
            show: true,
            locked: false,
            resize: true,
            export: true,
            code: "import_account_external_id",
            functionString: 'return row?.sourceEntityId',
            render: (item) => {
                return (
                    <span>{item.sourceEntityId}</span>
                )
            }
        },
        {
            key: "company_name",
            field: "company { id name sm username }",
            title: messages['import_account_label'],
            width: 200,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            code: "import_account_label",
            functionString: 'return row?.company?.name',
            render: (item) => {
                let name = item?.company?.name
                if (name?.length >= 7) {
                    name = name?.substring(0, 40) + "...";
                }
                return (
                    <NavLink to={`/app/accounts/overview/${item?.company?.id}`} style={{ color: 'black' }}>
                        <span style={{ textDecoration: 'underline', cursor: 'pointer' }}>{name}</span>
                    </NavLink>
                )
            }
        },
        {
            key: "createdOn",
            field: "createdOn",
            title: messages['import_account_imported_on'],
            width: 200,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            code: "import_account_imported_on",
            functionString: 'return row?.createdOn',
            render: (item) => {
                return (
                    <span>{item?.createdOn ? getDateFormatted(item?.createdOn, "date", "datetime", null, item?.timezone) : ''}</span>
                )
            }
        },
        {
            key: "updatedOn",
            field: "updatedOn",
            title: messages['import_account_updated_on'],
            width: 200,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            code: "import_account_updated_on",
            functionString: 'return row?.updatedOn',
            render: (item) => {
                return (
                    <span>{item?.updatedOn ? getDateFormatted(item?.updatedOn, "date", "datetime", null, item?.timezone) : ''}</span>
                )
            }
        },
        {
            key: "contacts",
            field: "contacts",
            title: messages['import_account_contacts'],
            width: 200,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            code: "import_account_contacts",
            functionString: 'return row?.contacts',
            render: (item) => {
                return (
                    <span>{item?.contacts || '0'}</span>
                )
            }
        },
        {
            key: "alerts",
            field: "alerts",
            title: messages['import_account_alerts'],
            width: 200,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            code: "import_account_alerts",
            functionString: 'return row?.alerts',
            render: (item) => {
                return (
                    <span>{item?.alerts || '0'}</span>
                )
            }
        },
        {
            key: "categories",
            field: "categories",
            title: messages['import_account_categories'],
            width: 200,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            code: "import_account_categories",
            functionString: 'return row?.categories',
            render: (item) => {
                return (
                    <span>{item?.categories || '0'}</span>
                )
            }
        },
        {
            key: "behavior",
            field: "behavior",
            title: messages['import_account_behavior'],
            width: 200,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            code: "import_account_behavior",
            functionString: 'return row?.behavior',
            render: (item) => {
                return (
                    <span>{item?.behavior || '0'}</span>
                )
            }
        },
        {
            key: "users",
            field: "users",
            title: messages['import_account_users'],
            width: 200,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            code: "import_account_users",
            functionString: 'return row?.users',
            render: (item) => {
                return (
                    <span>{item?.users || '0'}</span>
                )
            }
        },
        {
            key: "drivers",
            field: "drivers",
            title: messages['import_account_drivers'],
            width: 200,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            code: "import_account_drivers",
            functionString: 'return row?.drivers',
            render: (item) => {
                return (
                    <span>{item?.drivers || '0'}</span>
                )
            }
        },
        {
            key: "devices",
            field: "devices",
            title: messages['import_account_devices'],
            width: 200,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            code: "import_account_devices",
            functionString: 'return row?.devices',
            render: (item) => {
                return (
                    <span>{item?.devices || '0'}</span>
                )
            }
        },
        {
            key: "geofences",
            field: "geofences",
            title: messages['import_account_geofences'],
            width: 200,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            code: "import_account_geofences",
            functionString: 'return row?.geofences',
            render: (item) => {
                return (
                    <span>{item?.geofences || '0'}</span>
                )
            }
        },
        {
            key: "maintenance_tasks",
            field: "maintenance_tasks",
            title: messages['import_account_maintenance'],
            width: 200,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            code: "import_account_maintenance",
            functionString: 'return row?.maintenance_tasks',
            render: (item) => {
                return (
                    <span>{item?.maintenance_tasks || '0'}</span>
                )
            }
        },
        {
            key: "road_speed_overrides",
            field: "road_speed_overrides",
            title: messages['import_account_speed_override'],
            width: 200,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            code: "import_account_speed_override",
            functionString: 'return row?.road_speed_overrides',
            render: (item) => {
                return (
                    <span>{item?.road_speed_overrides || '0'}</span>
                )
            }
        },
        {
            key: "exceptions",
            field: "exceptions",
            title: messages['import_account_exceptions'],
            width: 200,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            code: "import_account_exceptions",
            functionString: 'return row?.exceptions',
            render: (item) => {
                return (
                    <a
                        style={{
                            textDecoration: item?.exceptions && 'underline',
                            cursor: item?.exceptions && 'pointer'
                        }}
                        onClick={() => {
                            item?.exceptions && dispatch(exceptionDetailsOpenModal(true, item))
                        }}
                    >
                        {item?.exceptions || '0'}
                    </a>
                )
            }
        },
    ]);
    const [searchFields, setSearchFields] = useState(["company_id", "sourceEntityId", "company_name"]);

    useEffect(() => {
        (!dealers || dealers.length === 0) && dispatch(searchEntityRequest({ key: "dealers" }));
    }, []);

    return (
        <Container maxWidth={false} className="container-view">
            {(loadingToken) && <Loading overlay show />}
            <Paper elevation={0}>
                <Grid
                    container
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="center"
                    style={{ padding: "14px 18px", borderBottom: "1px solid #e8e8e8", height: '95v' }}
                >
                    <Grid item xs={6}>
                        <Typography variant="h5" gutterBottom component="div">
                            {messages['import_account_title']} ({total})
                        </Typography>
                    </Grid>
                    <Grid item xs={6} className="main-view-buttons" style={{ display: 'flex', alignItems: 'center' }}>
                        <Button
                            className="btn-dark-blue"
                            onClick={() => {
                                dispatch(accountsImportOpenModal(true))
                            }}
                            disabled={!HasPermission(user, "impp4", "c")}
                        >
                            {messages['import_account_import_account']}
                        </Button>
                    </Grid>

                    <div style={{ width: '100%', marginBottom: 10, marginTop: 5 }}>
                        <Divider style={{ backgroundColor: '#999999' }} />
                    </div>
                    <DataView
                        key={"pm_main_import_p4"}
                        ref={tableRef}
                        exportName="importp4"
                        dinamicQuery
                        noAutomaticSearch
                        name="pm-import-p4-v2"
                        entity="import-p4"
                        graphqlFunction="searchCompanyMigrations"
                        graphqlSort="CompanyMigrationSortInput"
                        graphqlConditions="CompanyMigrationSearchInput"
                        customQuery="id sourceName sourceEntityName sourceEntityId newEntityName  newEntityId targetAccountId company { id dealerId username email }"
                        columns={columns}
                        hideAllInPagination
                        onChangeConditions={(tags, conditions, total, newColumns, rows, sort) => {
                            //current conditions
                            let currentConditions = {}
                            let currentTags = {}
                            if (!conditions && tags) {
                                currentConditions = tableRef?.current?.conditions;
                                currentTags = tags
                            } else {
                                currentConditions = conditions || [];
                                currentTags = currentConditions?.filter((x) => x?.tags)[0]?.tags || [];
                                if (currentTags && currentTags.length > 0) tableRef?.current?.setTags(currentTags);
                            }

                            currentConditions = currentConditions?.filter((item) => item?.fields[0] !== 'id' && item?.fields[0] !== 'company_id');
                            let tagsById = currentTags.filter((item) => item.company);
                            if (tagsById?.length > 0) {
                                //New Items
                                let currentIds = tagsById.map((item) => (item.id));
                                if (currentIds.length > 0) {
                                    currentConditions.push({ fields: ["id"], terms: currentIds, exact: true, tags: currentTags, conditionOperator: "OR" });
                                }
                            }
                            let tagsByLabel = currentTags.filter((item) => !item.company);
                            if (tagsByLabel.length > 0) {
                                let currentLabels = tagsByLabel.map((item) => (item["company { name }"]))
                                if (currentLabels.length > 0) {
                                    currentConditions.push({ fields: searchFields, terms: currentLabels, exact: false, tags: currentTags, conditionOperator: "OR" })
                                }
                            }

                            if (!currentConditions && tags) tableRef?.current?.getData(null, null, null, currentConditions);
                            else tableRef?.current?.getData(null, rows, sort, currentConditions, newColumns);


                        }}
                        noSplit
                        topheight={200}
                        onChangeTotal={(total) => setTotal(total)}
                        noFilters
                        onChangeColumns={(items) => setColumns(items)}
                        tableButtons={['orderColumn', 'export']}
                        //search
                        asyncSelect
                        searchConditions={["company_id", "company_name", "sourceEntityId"]}
                        searchFields={searchFields}
                        searchOptionValue="company { id }"
                        searchOptionLabel="company { name }"
                        extraField=" id company { id name }"
                        menuOptions={() => {
                            return [
                                messages['accounts_main_authenticate_cloud'],
                            ]
                        }}
                        onClickMenu={(event, item, eventClick) => {
                            if (event === messages['accounts_main_authenticate_cloud']) {
                                if (HasPermission(user, "cmplc")) {
                                    handleAuthenticate(eventClick, item?.company?.dealerId, item?.company?.email, `PX_${item?.company?.id}_${item?.id}`);
                                } else {
                                    NotificationManager.warning(
                                        messages['common_error_permission'],
                                        messages['accounts_main_authenticate_cloud'],
                                        5000,
                                    );
                                }
                            }
                        }}
                    />
                </Grid>
            </Paper>
            <NewMigration getData={tableRef?.current?.getData} />
            <ExceptionDetails />
        </Container>
    )
}
export default ImportP4Main;